// Die JSON-Schnittstelle des Servers vom Client aus gesehen (docs/architektur-paket-2.md,
// Abschnitt „HTTP-Schnittstelle“). Jede Funktion liefert entweder einen Wert oder eine fertige
// deutsche Meldung – nie eine Ausnahme, auch nicht bei Netzfehlern oder unbrauchbaren Antworten.
//
// Das Sitzungscookie setzt und liest der Browser selbst (httpOnly); hier kommt es nie vor, und es
// darf auch nirgends geloggt werden. Die Hol-Funktion ist einsetzbar, damit die Prüfungen ohne
// Netz laufen können.
import { type AvatarColors } from '@bauernhof/shared';

/** Antwort, wie `fetch` sie liefert – nur die Teile, die hier gebraucht werden. */
export interface AntwortAehnlich {
  status: number;
  ok: boolean;
  json(): Promise<unknown>;
}

/** Der Ausschnitt von `fetch`, den diese Datei braucht; der echte `fetch` erfüllt ihn. */
export type HolFunktion = (
  pfad: string,
  optionen?: { method?: string; headers?: Record<string, string>; body?: string },
) => Promise<AntwortAehnlich>;

/** Was `GET /api/ich` liefert. */
export interface IchDaten {
  name: string;
  parzelle: number | null;
  avatar: AvatarColors;
  avatarGewaehlt: boolean;
}

/** Warum keine Sitzung gilt; kommt bei 401 als `grund` mit. */
export type Sitzungsgrund = 'keine' | 'anderswo' | 'abgemeldet';

export type ApiAntwort<T> =
  | { ok: true; wert: T }
  | { ok: false; status: number; fehler: string; grund: Sitzungsgrund | null };

const MELDUNG_NETZ = 'Der Server antwortet gerade nicht. Bitte gleich noch einmal versuchen.';
const MELDUNG_UNKLAR = 'Da ist etwas schiefgegangen. Bitte noch einmal versuchen.';

/** Der echte `fetch` des Browsers als Standard; Prüfungen geben eine Attrappe herein. */
export function browserHol(): HolFunktion {
  return (pfad, optionen) => fetch(pfad, optionen);
}

function istFelder(wert: unknown): wert is Record<string, unknown> {
  return typeof wert === 'object' && wert !== null && !Array.isArray(wert);
}

/** Liest `ich` aus einer Antwort; null, wenn die Antwort nicht so aussieht. */
function leseIchDaten(wert: unknown): IchDaten | null {
  if (!istFelder(wert)) return null;
  const { name, parzelle, avatar, avatarGewaehlt } = wert;
  if (typeof name !== 'string' || !istFelder(avatar) || typeof avatarGewaehlt !== 'boolean') return null;
  if (parzelle !== null && (typeof parzelle !== 'number' || !Number.isInteger(parzelle))) return null;
  const farben = avatar as Record<keyof AvatarColors, unknown>;
  for (const bereich of ['haut', 'haar', 'oberteil', 'hose'] as const) {
    if (typeof farben[bereich] !== 'string') return null;
  }
  return { name, parzelle, avatar: avatar as unknown as AvatarColors, avatarGewaehlt };
}

function leseGrund(wert: unknown): Sitzungsgrund | null {
  if (wert === 'keine' || wert === 'anderswo' || wert === 'abgemeldet') return wert;
  return null;
}

/** Ruft die Schnittstelle auf und liest Status und Körper; Netzfehler werden zur Meldung. */
async function ruf(
  hol: HolFunktion,
  methode: string,
  pfad: string,
  koerper?: object,
): Promise<{ status: number; daten: unknown } | { netzfehler: true }> {
  try {
    const antwort = await hol(pfad, {
      method: methode,
      ...(koerper === undefined
        ? {}
        : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(koerper) }),
    });
    let daten: unknown = null;
    try {
      daten = await antwort.json();
    } catch {
      daten = null; // 204 und Fehlerseiten ohne JSON: der Status entscheidet.
    }
    return { status: antwort.status, daten };
  } catch {
    return { netzfehler: true };
  }
}

/** Die Fehlermeldung aus `{ fehler }` oder ein Ersatzsatz. */
function meldungAus(daten: unknown): string {
  if (istFelder(daten) && typeof daten['fehler'] === 'string' && daten['fehler'] !== '') return daten['fehler'];
  return MELDUNG_UNKLAR;
}

/** `GET /api/ich`: Wer ist angemeldet? Bei 401 steht der Grund in der Antwort. */
export async function holeIch(hol: HolFunktion): Promise<ApiAntwort<IchDaten>> {
  const antwort = await ruf(hol, 'GET', '/api/ich');
  if ('netzfehler' in antwort) return { ok: false, status: 0, fehler: MELDUNG_NETZ, grund: null };
  if (antwort.status === 200) {
    const ich = leseIchDaten(antwort.daten);
    if (ich === null) return { ok: false, status: 200, fehler: MELDUNG_UNKLAR, grund: null };
    return { ok: true, wert: ich };
  }
  const grund = antwort.status === 401 ? (leseGrund(istFelder(antwort.daten) ? antwort.daten['grund'] : null) ?? 'keine') : null;
  return { ok: false, status: antwort.status, fehler: meldungAus(antwort.daten), grund };
}

/**
 * Schickt ein Formular ab und holt danach `ich`: Der Erfolgskörper von `anmelden` und
 * `registrieren` nennt nur den Namen, der Client braucht aber auch Parzelle und Avatar. Ein
 * zweiter Aufruf ist billiger als zwei Wege, dieselben Angaben zu bekommen.
 */
async function meldeAnUndHoleIch(
  hol: HolFunktion,
  pfad: string,
  koerper: object,
  erfolgsStatus: number,
): Promise<ApiAntwort<IchDaten>> {
  const antwort = await ruf(hol, 'POST', pfad, koerper);
  if ('netzfehler' in antwort) return { ok: false, status: 0, fehler: MELDUNG_NETZ, grund: null };
  if (antwort.status !== erfolgsStatus) {
    return { ok: false, status: antwort.status, fehler: meldungAus(antwort.daten), grund: null };
  }
  return holeIch(hol);
}

/** `POST /api/anmelden`. */
export function sendeAnmelden(hol: HolFunktion, eingabe: { name: string; passwort: string }): Promise<ApiAntwort<IchDaten>> {
  return meldeAnUndHoleIch(hol, '/api/anmelden', eingabe, 200);
}

/** `POST /api/registrieren`. */
export function sendeRegistrieren(
  hol: HolFunktion,
  eingabe: { name: string; passwort: string; einladungscode: string },
): Promise<ApiAntwort<IchDaten>> {
  return meldeAnUndHoleIch(hol, '/api/registrieren', eingabe, 201);
}

/** `POST /api/passwort-neu`: setzt das Passwort mit dem Rücksetzcode und meldet gleich an. */
export function sendePasswortNeu(
  hol: HolFunktion,
  eingabe: { name: string; code: string; passwort: string },
): Promise<ApiAntwort<IchDaten>> {
  return meldeAnUndHoleIch(hol, '/api/passwort-neu', eingabe, 200);
}

/**
 * `PUT /api/avatar`: die im Spiegel gewählten Farben ans Konto. Der Server nimmt nur Farben aus
 * `AVATAR_PALETTE` an; eine Ablehnung kommt als deutscher Satz zurück, nie als Ausnahme.
 */
export async function sendeAvatar(hol: HolFunktion, farben: AvatarColors): Promise<ApiAntwort<null>> {
  const antwort = await ruf(hol, 'PUT', '/api/avatar', farben);
  if ('netzfehler' in antwort) return { ok: false, status: 0, fehler: MELDUNG_NETZ, grund: null };
  if (antwort.status !== 200) {
    const grund = antwort.status === 401 ? (leseGrund(istFelder(antwort.daten) ? antwort.daten['grund'] : null) ?? 'keine') : null;
    return { ok: false, status: antwort.status, fehler: meldungAus(antwort.daten), grund };
  }
  return { ok: true, wert: null };
}

/** `POST /api/abmelden`; wahr, wenn der Server die Sitzung beendet hat. */
export async function sendeAbmelden(hol: HolFunktion): Promise<boolean> {
  const antwort = await ruf(hol, 'POST', '/api/abmelden');
  return 'status' in antwort && antwort.status === 204;
}
